import React from "react";
import { Grid, TablePagination } from "@material-ui/core";
import { inject, observer } from "mobx-react";
import { Values } from "@src/Tools/Values";
import STPageHeader from "@src/Components/Ui/STPageHeader";
import { IAuthStore } from "@src/Stores/Auth/types";
import { observable } from "mobx";
import { IManagementStore } from "@src/Stores/Management/types";
import AppPreloader from "@src/Components/Common/AppPreloader/AppPreloader";
import { IValuesStore } from "@src/Stores/Values/types";
import AuditLogTable from "./AuditLogTable/AuditLogTable";
import { AuditLog as AuditLogEntity, AuditLogAction, AuditLogRecordsRequest } from "@src/Interfaces/Entity/AuditLog";
import WebSocketsProxy from "@src/Tools/WebSocketsProxy";
import ExportService from "@src/Tools/ExportService";
import CheckedIcon from "@material-ui/icons/Check";
import _ from "lodash";
import AuditLogFilter from "./AuditLogFilter/AuditLogFilter";
import {
  PendingReviewsRootGrid,
  PendingReviewsTitleText,
  ExportAndFilterRootGrid,
  ButtonExportRootGrid,
  ButtonExport,
} from "./styles";

interface IProps {
  auth?: IAuthStore;
  management?: IManagementStore;
  values?: IValuesStore;
}

@inject("auth", "management", "values")
@observer
class AuditLog extends React.Component<IProps> {
  @observable isLoading: boolean = true;
  @observable isMounted: boolean = false;
  @observable auditLogs: AuditLogEntity[] = [];
  @observable auditLogsCount: number;
  @observable pendingReviewsAuditLogs: AuditLogEntity[] = [];
  @observable defaultPendingReviewsAuditLogs: AuditLogEntity[] = [];
  @observable defaultSeverityFilterValue: string = Values.getLocalizationValue("all");
  @observable severityFilterValue: string = this.defaultSeverityFilterValue;
  @observable defaultTypeFilterValue: string = Values.getLocalizationValue("all");
  @observable typeFilterValue: string = this.defaultTypeFilterValue;
  @observable exportedAuditLogData: any[] = [];
  @observable exportedAuditLogDataHeaders: any[] = [];
  @observable isAllRecordsSelected: boolean = false;

  private fileName = `${Values.getLocalizationValue("auditLog")}.xlsx`;
  private webSocketsProxy = new WebSocketsProxy();
  private reviewedEventName = "AuditLogReviewed";
  private tablePage = 0;
  private tablePageSize = 15;

  requestTableData = async (useSeverity?: boolean) => {
    const auditLogRecordsRequest: AuditLogRecordsRequest = {
      recordsPeriod: this.props.values.auditLogRecordsPeriod,
      pageSize: this.tablePageSize,
      page: this.tablePage,
      severity: useSeverity ? parseInt(this.severityFilterValue, 10) : null,
    };
    const { items, totalItems } = _.cloneDeep(await this.props.management.getAuditLogs(auditLogRecordsRequest));
    this.auditLogs = items;
    this.auditLogsCount = totalItems;
  };

  async componentDidMount() {
    this.isMounted = true;
    if (this.isMounted) {
      await this.props.management.getLocations();
      await this.props.management.getPersons();
      await this.requestTableData();
      this.pendingReviewsAuditLogs = _.cloneDeep(await this.props.management.getNeedReviewAuditLogs());
      this.defaultPendingReviewsAuditLogs = _.cloneDeep(this.pendingReviewsAuditLogs);
      document.title = Values.getLocalizationValue("auditLog");
      this.isLoading = false;
      this.webSocketsProxy.subscribe(this.reviewedEventName, (event) => this.handleWsReviewChecked(event));
    }
  }

  async componentWillUnmount() {
    this.webSocketsProxy.unsubscribe(this.reviewedEventName, (event) => this.handleWsReviewChecked(event));
    this.isMounted = false;
  }

  handleSeverityFilterChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    this.severityFilterValue = value;
    this.tablePage = 0;
    if (value === this.defaultSeverityFilterValue) {
      this.requestTableData();
      return;
    }
    this.requestTableData(true);
  };

  handleTypeFilterChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === this.defaultTypeFilterValue) {
      this.typeFilterValue = this.defaultTypeFilterValue;
      this.pendingReviewsAuditLogs = _.cloneDeep(this.defaultPendingReviewsAuditLogs);
      return;
    }
    this.typeFilterValue = value;
    this.pendingReviewsAuditLogs = _.cloneDeep(
      this.defaultPendingReviewsAuditLogs.filter((log) => log.type === parseInt(value))
    );
    this.isAllRecordsSelected = false;
  };

  handleGetColor = (severity: number) => {
    return {
      backgroundColor: this.props.values.auditLogSeverityColors[severity],
    };
  };

  handleGetLocationName = (locationId: string) => {
    const location = this.props.management.locations.find((location) => location.id === locationId);
    return location ? location.name : "";
  };

  handleGetSeverity = (severity: number) => {
    return Object.entries(this.props.values.auditLogSeverities).filter(
      (entry: [string, number]) => entry[1] === severity
    )[0][0];
  };

  handleGetType = (type: number) => {
    return Object.entries(this.props.values.auditLogTypes).filter((entry: [string, number]) => entry[1] === type)[0][0];
  };

  actionToString = (action: AuditLogAction) => {
    let resultMessage = Values.getLocalizationValue(action.message);
    const { auth } = this.props;
    (action.args || []).forEach((item, index) => {
      let updatedItem = item;
      var regExp = /\(([^)]+)\)/;
      var matches = regExp.exec(item);
      if (matches) {
        const personItem = this.props.management.persons.find(
          (person) =>
            (person.name === matches[1] || person.aliasNames.find((alias) => alias.name === matches[1])) &&
            person.id === auth.user.person.id
        );
        if (personItem) {
          updatedItem = `${auth.user.person.fullName} (${auth.user.person.name})`;
        }
      } else {
        const personItem = this.props.management.persons.find(
          (person) =>
            (person.name === item || person.aliasNames.find((alias) => alias.name === item)) &&
            person.id === auth.user.person.id
        );
        if (personItem) {
          updatedItem = auth.user.person.name;
        }
      }
      resultMessage = resultMessage.replace(`{${index}}`, updatedItem);
    });
    return resultMessage;
  };

  handleGetReviewBy = (reviewedBy: string) => {
    let resultMessage = Values.getLocalizationValue("reviewedBy");
    const { auth } = this.props;
    let updatedReviewedBy = reviewedBy;
    var regExp = /\(([^)]+)\)/;
    var matches = regExp.exec(reviewedBy);
    const personItem = this.props.management.persons.find(
      (person) =>
        (person.name === matches[1] || person.aliasNames.find((alias) => alias.name === matches[1])) &&
        person.id === auth.user.person.id
    );
    if (personItem) {
      updatedReviewedBy = `${auth.user.person.fullName} (${auth.user.person.name})`;
    }
    resultMessage = resultMessage.replace("{0}", updatedReviewedBy);
    return resultMessage;
  };

  handleReviewClick = async () => {
    const selectedAuditLogRecords = _.cloneDeep(
      this.pendingReviewsAuditLogs.filter((log) => log.checked).map((log) => log.id)
    );
    const reviewedAuditLog = _.cloneDeep(await this.props.management.reviewAuditLogs(selectedAuditLogRecords));
    this.auditLogs = this.auditLogs.map((log) => {
      const filteredLog = reviewedAuditLog.find((auditLog) => auditLog.id === log.id);
      if (log.id && !filteredLog) {
        return log;
      }
      log.needReview = false;
      log.reviewedBy = filteredLog.reviewedBy;
      return log;
    });
    this.pendingReviewsAuditLogs = _.cloneDeep(await this.props.management.getNeedReviewAuditLogs());
    this.isAllRecordsSelected = false;
    this.forceUpdate();
  };

  async handleWsReviewChecked(event: CustomEvent) {
    const auditLogs: AuditLogEntity[] = event.detail.auditLogs;
    this.auditLogs = this.auditLogs.map((log) => {
      const filteredLog = auditLogs.find((auditLog) => auditLog.id === log.id);
      if (log.id && !filteredLog) {
        return log;
      }
      log.needReview = false;
      log.reviewedBy = filteredLog.reviewedBy;
      return log;
    });
    this.pendingReviewsAuditLogs = _.cloneDeep(await this.props.management.getNeedReviewAuditLogs());
    this.forceUpdate();
  }

  handleExport = async () => {
    const response = _.cloneDeep(await this.props.management.getAuditLogs());
    this.exportedAuditLogData = response.items.map((log) => ({
      date: new Date(log.time).toLocaleString(),
      type: this.handleGetType(log.type),
      severity: this.handleGetSeverity(log.severity),
      action: this.actionToString(log.action),
      reviewed: log.reviewedBy ? Values.getLocalizationValue("reviewedBy").replace("{0}", log.reviewedBy) : "",
    }));
    ExportService.exportToExcel(this.exportedAuditLogData, this.fileName);
  };

  handleChangePage = async (event: unknown, newPage: number) => {
    this.tablePage = newPage;
    if (this.severityFilterValue === this.defaultSeverityFilterValue) {
      this.requestTableData();
      return;
    }
    this.requestTableData(true);
  };

  handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    this.tablePageSize = +event.target.value;
    this.tablePage = 0;
    if (this.severityFilterValue === this.defaultSeverityFilterValue) {
      this.requestTableData();
      return;
    }
    this.requestTableData(true);
  };

  handleSelectedCheckboxChange = (auditLogRecordId: string) => {
    this.pendingReviewsAuditLogs = this.pendingReviewsAuditLogs.map((logRecord) => {
      if (logRecord.id !== auditLogRecordId) {
        return logRecord;
      }
      logRecord.checked = !Boolean(logRecord.checked);
      return logRecord;
    });
    this.isAllRecordsSelected = false;
    this.forceUpdate();
  };

  handleSelectedAllCheckboxChange = () => {
    const { auth } = this.props;
    const currentUserId = auth.user.person.id;
    this.isAllRecordsSelected = !Boolean(this.isAllRecordsSelected);
    this.pendingReviewsAuditLogs = this.pendingReviewsAuditLogs.map((logRecord) => {
      if (currentUserId === logRecord.createdBy) {
        return logRecord;
      }
      logRecord.checked = this.isAllRecordsSelected;
      return logRecord;
    });
    this.forceUpdate();
  };

  render() {
    const { values, auth } = this.props;
    const currentUserId = auth.user.person.id;
    const hasAnyPendingReviewData: boolean = this.defaultPendingReviewsAuditLogs.length > 0;

    if (this.isLoading) {
      return <AppPreloader />;
    }
    return (
      <Grid container>
        <Grid item xs={12}>
          <STPageHeader>{Values.getLocalizationValue("auditLog")}</STPageHeader>
        </Grid>
        {hasAnyPendingReviewData && (
          <PendingReviewsRootGrid item xs={12}>
            <PendingReviewsTitleText>{Values.getLocalizationValue("pendingReviews")}</PendingReviewsTitleText>
            <ExportAndFilterRootGrid container>
              <ButtonExportRootGrid item xs={6}>
                <ButtonExport color="primary" onClick={this.handleReviewClick}>
                  <CheckedIcon />
                  {Values.getLocalizationValue("review")}
                </ButtonExport>
              </ButtonExportRootGrid>
              <Grid item xs={6}>
                <AuditLogFilter
                  title={Values.getLocalizationValue("type")}
                  handleChange={this.handleTypeFilterChange}
                  entities={values.auditLogTypes}
                  defaultValue={this.defaultTypeFilterValue}
                  value={this.typeFilterValue}
                />
              </Grid>
            </ExportAndFilterRootGrid>
            <AuditLogTable
              currentLocation={auth.user.location}
              currentUserId={currentUserId}
              auditLogData={this.pendingReviewsAuditLogs}
              handleGetSeverity={this.handleGetSeverity}
              handleGetType={this.handleGetType}
              handleGetColor={this.handleGetColor}
              actionToString={this.actionToString}
              isPendingReviewTable={true}
              handleSelectedCheckboxChange={this.handleSelectedCheckboxChange}
              handleSelectedAllCheckboxChange={this.handleSelectedAllCheckboxChange}
              handleGetLocationName={this.handleGetLocationName}
              isAllRecordsSelected={this.isAllRecordsSelected}
              handleGetReviewBy={this.handleGetReviewBy}
            />
          </PendingReviewsRootGrid>
        )}
        <Grid item xs={12}>
          <ExportAndFilterRootGrid container>
            <ButtonExportRootGrid item xs={6}>
              <ButtonExport color="primary" onClick={this.handleExport}>
                {Values.getLocalizationValue("export")}
              </ButtonExport>
            </ButtonExportRootGrid>
            <Grid item xs={6}>
              <AuditLogFilter
                title={Values.getLocalizationValue("severity")}
                handleChange={this.handleSeverityFilterChange}
                entities={values.auditLogSeverities}
                defaultValue={this.defaultSeverityFilterValue}
                value={this.severityFilterValue}
              />
            </Grid>
          </ExportAndFilterRootGrid>
          <Grid item xs={12}>
            <AuditLogTable
              currentLocation={auth.user.location}
              currentUserId={currentUserId}
              handleGetReviewBy={this.handleGetReviewBy}
              auditLogData={this.auditLogs}
              handleGetType={this.handleGetType}
              handleGetSeverity={this.handleGetSeverity}
              handleGetColor={this.handleGetColor}
              actionToString={this.actionToString}
              handleGetLocationName={this.handleGetLocationName}
              isPendingReviewTable={false}
            />
            <TablePagination
              rowsPerPageOptions={[5, 15, 25, 50]}
              component="div"
              count={this.auditLogsCount}
              rowsPerPage={this.tablePageSize}
              page={this.tablePage}
              onChangePage={this.handleChangePage}
              onChangeRowsPerPage={this.handleChangeRowsPerPage}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

export default AuditLog;
